import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { AreaChart } from '@repletoryreach/sdk-ui/charts';
import { media } from '@repletoryreach/sdk-api';
import { StatCard } from '../../../components/StatCard';
import { TenantName } from '../../../components/TenantName';
import { formatDateTime, formatInteger, formatRelative, humanBytes } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
const WINDOWS: media.MediaScanWindow[] = ['24h', '7d', '30d', '90d'];
const FEED_LIMIT = 25;

const icon = (path: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);
const ShieldIcon = icon(<><path d="M10 2.5 4.5 4.8V10c0 3.4 2.3 6 5.5 7.5 3.2-1.5 5.5-4.1 5.5-7.5V4.8z" /><path d="M7.5 10l1.8 1.8L13 8" /></>);
const VirusIcon = icon(<><circle cx="10" cy="10" r="4.5" /><path d="M10 2.2v1.9M10 15.9v1.9M2.2 10h1.9M15.9 10h1.9M4.5 4.5l1.3 1.3M14.2 14.2l1.3 1.3M15.5 4.5l-1.3 1.3M5.8 14.2l-1.3 1.3" /></>);
const QuestionIcon = icon(<><circle cx="10" cy="10" r="7.5" /><path d="M8 7.8a2 2 0 1 1 2.6 1.9c-.4.2-.6.6-.6 1v.6M10 14.2v.4" /></>);
const RateIcon = icon(<><path d="M5 15 15 5" /><circle cx="6.5" cy="6.5" r="2" /><circle cx="13.5" cy="13.5" r="2" /></>);

/**
 * Virus scanning, on the platform Media page.
 *
 * Every upload was scanned and nothing recorded the outcome, so the page could
 * describe what is stored and say nothing about what was turned away. An
 * infected upload is refused before anything is persisted, which means it has
 * no `media_objects` row anywhere: `media_scan_events` is the only record it
 * ever happened, and these are the only figures that count it.
 *
 * The counts are computed by the service over the chosen window, not by this
 * component over a page of rows.
 */
export function ScanPanel() {
  const t = useT();
  const [window, setWindow] = useState<media.MediaScanWindow>('7d');
  const [verdict, setVerdict] = useState<media.MediaScanVerdict | ''>('');

  const summaryQ = useQuery({
    queryKey: ['admin', 'media', 'scans', 'summary', window],
    queryFn: () => media.scanSummary({ window }),
    staleTime: 60_000
  });

  const feedQ = useQuery({
    queryKey: ['admin', 'media', 'scans', 'feed', window, verdict],
    queryFn: () => media.listScans({
      limit: FEED_LIMIT,
      ...(verdict ? { verdict } : {}),
      ...(summaryQ.data?.data.since ? { since: summaryQ.data.data.since } : {})
    }),
    enabled: summaryQ.isSuccess,
    staleTime: 30_000
  });

  const s = summaryQ.data?.data;
  const rows = (feedQ.data?.data ?? []) as Indexed<media.MediaScanEvent>[];
  const failed = Boolean(summaryQ.error);
  // A figure that could not be loaded is not a zero, and "no infected files"
  // is exactly the claim this panel must never make by accident.
  const unknown = summaryQ.isLoading || failed;
  const metric = (value: string) => (unknown ? t('common.notAvailable') : value);

  const series = useMemo(
    () => (s?.by_day ?? []).map((d) => ({
      day: d.day,
      clean: d.clean,
      infected: d.infected,
      unscanned: d.unscanned
    })),
    [s]
  );

  const verdictTone = (v: media.MediaScanVerdict): StatusTone =>
    v === 'infected' ? 'down' : v === 'unscanned' ? 'warning' : 'healthy';

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={4}>
            <Title order={4}>{t('media.scan.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('media.scan.description')}</Text>
          </Stack>
          <SegmentedControl
            size="xs"
            value={window}
            onChange={(v) => setWindow(v as media.MediaScanWindow)}
            data={WINDOWS.map((w) => ({ value: w, label: t(`media.scan.window.${w}`) }))}
          />
        </Group>

        {failed && (
          <Alert color="error">
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <span>{t('media.scan.loadError')}</span>
              <Button size="xs" variant="outline" onClick={() => void summaryQ.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16 }}>
          <StatCard
            loading={summaryQ.isLoading}
            tone="success"
            icon={ShieldIcon}
            label={t('media.scan.kpi.clean')}
            value={metric(formatInteger(s?.clean ?? 0))}
            hint={unknown ? undefined : t('media.scan.kpi.cleanHint', { bytes: humanBytes(s?.bytes_scanned ?? 0) })}
            withRule
          />
          <StatCard
            loading={summaryQ.isLoading}
            tone="error"
            icon={VirusIcon}
            label={t('media.scan.kpi.infected')}
            value={metric(formatInteger(s?.infected ?? 0))}
            hint={unknown ? undefined : t('media.scan.kpi.infectedHint')}
            withRule
          />
          <StatCard
            loading={summaryQ.isLoading}
            tone="warning"
            icon={QuestionIcon}
            label={t('media.scan.kpi.unscanned')}
            value={metric(formatInteger(s?.unscanned ?? 0))}
            hint={unknown ? undefined : t('media.scan.kpi.unscannedHint')}
            withRule
          />
          <StatCard
            loading={summaryQ.isLoading}
            tone={(s?.infected ?? 0) > 0 ? 'error' : 'neutral'}
            icon={RateIcon}
            label={t('media.scan.kpi.rate')}
            value={metric(`${(s?.infected_rate ?? 0).toFixed(2)}%`)}
            hint={unknown ? undefined : t('media.scan.kpi.rateHint', { count: formatInteger(s?.total ?? 0) })}
            withRule
          />
        </div>

        {/* Where the record starts. The log is not backfilled, so an upload
            from before this instant is in none of the counts above, and a
            reader must not take a quiet window for a clean one. */}
        {!unknown && (
          <Text role="caption" c={MUTED}>
            {s?.first_event_at
              ? t('media.scan.since', { when: formatDateTime(s.first_event_at) })
              : t('media.scan.noRecordYet')}
          </Text>
        )}

        {series.length > 1 && (
          <AreaChart
            data={series}
            dataKey="day"
            h={200}
            curveType="monotone"
            series={[
              { name: 'clean', label: t('media.scan.kpi.clean'), color: 'var(--rr-color-success)' },
              { name: 'infected', label: t('media.scan.kpi.infected'), color: 'var(--rr-color-error)' },
              { name: 'unscanned', label: t('media.scan.kpi.unscanned'), color: 'var(--rr-color-warning)' }
            ]}
          />
        )}

        {(s?.top_signatures?.length ?? 0) > 0 && (
          <Group gap="xs" wrap="wrap" align="center">
            <Text role="caption" c={MUTED}>{t('media.scan.signatures')}</Text>
            {s!.top_signatures.map((sig) => (
              <Badge key={sig.signature} size="xs" color="red" variant="light">
                {sig.signature} · {formatInteger(sig.count)}
              </Badge>
            ))}
          </Group>
        )}

        <Stack gap="sm">
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Text role="inputLabel">{t('media.scan.feed.title')}</Text>
            <SegmentedControl
              size="xs"
              value={verdict}
              onChange={(v) => setVerdict(v as media.MediaScanVerdict | '')}
              data={[
                { value: '', label: t('common.all') },
                { value: 'infected', label: t('media.scan.kpi.infected') },
                { value: 'unscanned', label: t('media.scan.kpi.unscanned') },
                { value: 'clean', label: t('media.scan.kpi.clean') }
              ]}
            />
          </Group>

          {!feedQ.isLoading && rows.length === 0 ? (
            <EmptyState title={t('media.scan.feed.empty')} description={t('media.scan.feed.emptyHint')} />
          ) : (
            <DataTable<Indexed<media.MediaScanEvent>>
              records={rows}
              loading={feedQ.isLoading}
              idAccessor="id"
              emptyState={t('media.scan.feed.empty')}
              columns={[
                {
                  accessor: 'verdict',
                  title: t('media.scan.columns.verdict'),
                  render: (r) => (
                    <StatusBadge status={verdictTone(r.verdict)} size="xs">
                      {t(`media.scan.verdict.${r.verdict}`)}
                    </StatusBadge>
                  )
                },
                {
                  accessor: 'organisation_id',
                  title: t('media.columns.tenant'),
                  render: (r) => <TenantName id={r.organisation_id} />
                },
                {
                  accessor: 'original_name',
                  title: t('media.scan.columns.file'),
                  render: (r) => r.original_name
                    ? <Text role="bodySmall" title={r.original_name}>{r.original_name}</Text>
                    : <Text role="caption" c={MUTED}>{r.mime_type ?? t('common.none')}</Text>
                },
                {
                  accessor: 'size_bytes',
                  title: t('media.columns.bytes'),
                  textAlign: 'right',
                  render: (r) => humanBytes(r.size_bytes)
                },
                {
                  accessor: 'signature',
                  title: t('media.scan.columns.detail'),
                  render: (r) => r.signature
                    ? <Badge size="xs" color="red" variant="light">{r.signature}</Badge>
                    : r.detail
                      ? <Text role="caption" c={MUTED} title={r.detail}>{r.detail}</Text>
                      : <Text role="caption" c={MUTED}>{t('common.none')}</Text>
                },
                {
                  accessor: 'media_id',
                  title: t('media.scan.columns.stored'),
                  // A refused upload has no stored object, and that is the
                  // whole point of the row: nothing entered the platform.
                  render: (r) => r.media_id
                    ? <Text role="caption" c={MUTED}>{t('common.yes')}</Text>
                    : <Badge size="xs" variant="light">{t('media.scan.notStored')}</Badge>
                },
                {
                  accessor: 'scanned_at',
                  title: t('media.scan.columns.when'),
                  render: (r) => (
                    <Tooltip label={formatDateTime(r.scanned_at)} position="top">
                      <span>{formatRelative(r.scanned_at)}</span>
                    </Tooltip>
                  )
                }
              ]}
            />
          )}

          {(feedQ.data?.meta?.capped ?? false) && (
            <Text role="caption" c={MUTED}>
              {t('media.scan.feed.capped', { shown: rows.length, total: formatInteger(feedQ.data!.meta.total) })}
            </Text>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
